import { useState } from "react";
import { format } from "date-fns";
import { 
  Calendar, 
  Clock, 
  Package, 
  ChevronDown, 
  ChevronUp, 
  CheckCircle, 
  AlertCircle, 
  XCircle, 
  Trash2,
  MapPin,
  ZoomIn
} from "lucide-react";
import { StorageImage } from "@/components/StorageImage";
import { SpecialHandlingIndicator } from "@/components/SpecialHandlingIndicator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { formatRetailerName } from "@/lib/formatRetailer";
import { getPickupStatusLabel } from "@/lib/pickupStatus";
import type { PackageWithSchedule, UserPickup } from "@/types/database";

interface PickupCardProps {
  pickup: UserPickup | null;
  packages: PackageWithSchedule[];
  onCancelPackage?: (pkg: PackageWithSchedule) => void;
  canCancelPackage?: (pkg: PackageWithSchedule) => boolean;
  className?: string;
}

export const PickupCard = ({ 
  pickup, 
  packages, 
  onCancelPackage,
  canCancelPackage,
  className 
}: PickupCardProps) => {
  const [isOpen, setIsOpen] = useState(true);

  const getStatusBadgeStyle = (status: string) => {
    switch (status) {
      case "confirmed":
        return "bg-green-100 text-green-800 border-green-200";
      case "awaiting_admin_confirmation":
        return "bg-amber-100 text-amber-800 border-amber-200";
      case "awaiting_confirmation":
      case "counter_proposed":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "completed":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "cancelled":
      case "auto_canceled":
        return "bg-gray-100 text-gray-600 border-gray-200";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const getApprovalBadge = (approvalStatus: string) => {
    switch (approvalStatus) {
      case "pending":
        return (
          <Badge variant="outline" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20 text-xs">
            <AlertCircle className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
      case "approved":
        return (
          <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20 text-xs">
            <CheckCircle className="w-3 h-3 mr-1" />
            Approved
          </Badge>
        );
      case "rejected":
        return (
          <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/20 text-xs">
            <XCircle className="w-3 h-3 mr-1" />
            Rejected
          </Badge>
        );
      default:
        return null;
    }
  };

  // If no pickup, show a different card state
  if (!pickup) {
    return (
      <Card className={cn("border-dashed", className)}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-muted-foreground" />
              No Pickup Scheduled
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {packages.length > 0 
              ? `You have ${packages.length} package${packages.length !== 1 ? 's' : ''} ready. Schedule a pickup when you're ready.`
              : "Add packages to schedule a pickup."}
          </p>
          
          {packages.length > 0 && (
            <div className="mt-4 space-y-2">
              {packages.map((pkg) => (
                <div 
                  key={pkg.id}
                  className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Package className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium text-sm">{formatRetailerName(pkg.retailer)}</span>
                    <SpecialHandlingIndicator size={pkg.size} />
                    {getApprovalBadge(pkg.approval_status)}
                  </div>
                  {canCancelPackage?.(pkg) && onCancelPackage && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8"
                      onClick={() => onCancelPackage(pkg)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  const pickupDate = new Date(pickup.proposed_date);
  const statusLabel = getPickupStatusLabel(pickup.status);
  const statusStyle = getStatusBadgeStyle(pickup.status);

  return (
    <Card className={cn("shadow-soft", className)}>
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">
                {format(pickupDate, "EEEE, MMMM d")}
              </CardTitle>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-0.5">
                <Clock className="w-3.5 h-3.5" />
                <span>{pickup.proposed_time_slot}</span>
              </div>
            </div>
          </div>
          <Badge variant="outline" className={cn("self-start", statusStyle)}>
            {statusLabel}
          </Badge>
        </div>

        {/* Address */}
        {pickup.address && (
          <div className="flex items-start gap-2 text-sm text-muted-foreground mt-3 pl-1">
            <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{pickup.address}</span>
          </div>
        )}
      </CardHeader>

      <CardContent className="pt-0">
        {/* Collapsible Packages List */}
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger asChild>
            <button className="flex items-center justify-between w-full py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              <span className="flex items-center gap-2">
                <Package className="w-4 h-4" />
                {packages.length} Package{packages.length !== 1 ? 's' : ''} in this pickup
              </span>
              {isOpen ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>
          </CollapsibleTrigger>
          
          <CollapsibleContent>
            <div className="space-y-2 pt-2">
              {packages.map((pkg) => (
                <div 
                  key={pkg.id}
                  className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border"
                >
                    <div className="flex items-center gap-3 min-w-0">
                     {pkg.package_photo_url && (
                        <div className="w-10 h-10 rounded-md bg-muted overflow-hidden border flex-shrink-0">
                          <StorageImage bucket="package-photos" storedValue={pkg.package_photo_url} alt="Package" className="w-full h-full object-cover" />
                        </div>
                      )}
                      {!pkg.package_photo_url && (
                        <Package className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <span className="font-medium text-sm flex items-center gap-1.5 truncate">
                          {formatRetailerName(pkg.retailer)}
                          <SpecialHandlingIndicator size={pkg.size} />
                        </span>
                        {pkg.package_description && (
                          <span className="text-xs text-muted-foreground block truncate">
                            {pkg.package_description}
                          </span>
                        )}
                      </div>
                    </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {getApprovalBadge(pkg.approval_status)}
                    {canCancelPackage?.(pkg) && onCancelPackage && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                        onClick={() => onCancelPackage(pkg)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              
              {packages.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No packages linked to this pickup
                </p>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
};
